/**
 * emailAudienceMockAdapter
 *
 * DASH-EMAIL-004 mock data source for the Email tab's "Audience &
 * Acquisition" view.
 *
 * Deliberately does NOT reuse emailPerformanceMockAdapter's
 * BROADCAST_ROWS_BASE — that catalog carries zero audience-dimension
 * fields (leadSource/acquisitionChannel/geography/lifecycleState) and
 * zero-audience-shaped counts (recipient records, not delivered emails).
 * This file is its own dedicated catalog, following the same "one
 * canonical source, derive everything else" discipline established by
 * emailDashboardMockAdapter.js/emailPerformanceMockAdapter.js.
 *
 * Naming: "Recipient Records", never "Total Recipients" — the mock model
 * is additive per-environment records, not a deduplicated unique-human
 * count (same naming rule established during Checkpoint 3.5A).
 *
 * Every row is fully specified by (environment, leadSource,
 * acquisitionChannel, geography, lifecycleState) — mirroring
 * emailPerformanceMockAdapter's BROADCAST_ROWS_BASE (each row = one
 * broadcast, fully specified by program+campaign+broadcast identity), not
 * a full dimensional cross-product. Filtering is a straightforward
 * subset-sum over matching rows, so "All >= any filtered subset" holds by
 * construction — no separately-tuned "all" baseline exists to drift out
 * of sync (the exact defect Checkpoint 3.5A's remediation retired
 * elsewhere in this Email surface).
 *
 * Environment is additive-union, not a scaling multiplier: "all" is
 * literally qualification's rows + production's rows, summed — never a
 * separately-authored total.
 */

const LEAD_SOURCES = ["organic", "paid_search", "referral", "partner"];
const ACQUISITION_CHANNELS = ["welcome_series", "promotional", "newsletter", "reengagement"];
const GEOGRAPHIES = ["northeast", "south", "midwest", "west"];
const LIFECYCLE_STATES = ["subscribed", "active", "engaged", "qualified", "customer"];

const LEAD_SOURCE_LABELS = {
  organic: "Organic",
  paid_search: "Paid Search",
  referral: "Referral",
  partner: "Partner",
};

const ACQUISITION_CHANNEL_LABELS = {
  welcome_series: "Welcome Series",
  promotional: "Promotional",
  newsletter: "Newsletter",
  reengagement: "Re-engagement",
};

const GEOGRAPHY_LABELS = {
  northeast: "Northeast",
  south: "South",
  midwest: "Midwest",
  west: "West",
};

const LIFECYCLE_STATE_LABELS = {
  subscribed: "Subscribed",
  active: "Active",
  engaged: "Engaged",
  qualified: "Qualified Lead",
  customer: "Customer",
};

// Deterministic, hand-authored per-environment segment catalog — same
// discipline as emailPerformanceMockAdapter's BROADCAST_ROWS_BASE and
// emailReputationMockAdapter's ENVIRONMENT_POOLS. `newRecords` and
// `unsubscribes` are the only two flows this catalog tracks directly;
// every rate/derived total (netGrowth, unsubscribeRate) is computed from
// these raw counts, never stored redundantly.
const AUDIENCE_SEGMENT_ROWS = Object.freeze([
  // Qualification (smaller volume, ~1/15th of Production — same ratio
  // convention as emailReputationMockAdapter's QUALIFICATION_POOL/
  // PRODUCTION_POOL).
  Object.freeze({ id: "qual-1", environment: "qualification", lifecycleState: "subscribed", leadSource: "organic", acquisitionChannel: "promotional", geography: "midwest", recipientRecords: 420, newRecords: 65, unsubscribes: 9 }),
  Object.freeze({ id: "qual-2", environment: "qualification", lifecycleState: "active", leadSource: "paid_search", acquisitionChannel: "newsletter", geography: "west", recipientRecords: 310, newRecords: 40, unsubscribes: 6 }),
  Object.freeze({ id: "qual-3", environment: "qualification", lifecycleState: "engaged", leadSource: "referral", acquisitionChannel: "reengagement", geography: "northeast", recipientRecords: 260, newRecords: 28, unsubscribes: 5 }),
  Object.freeze({ id: "qual-4", environment: "qualification", lifecycleState: "qualified", leadSource: "partner", acquisitionChannel: "welcome_series", geography: "south", recipientRecords: 190, newRecords: 22, unsubscribes: 3 }),
  Object.freeze({ id: "qual-5", environment: "qualification", lifecycleState: "customer", leadSource: "organic", acquisitionChannel: "promotional", geography: "midwest", recipientRecords: 150, newRecords: 8, unsubscribes: 2 }),
  Object.freeze({ id: "qual-6", environment: "qualification", lifecycleState: "subscribed", leadSource: "paid_search", acquisitionChannel: "newsletter", geography: "west", recipientRecords: 380, newRecords: 58, unsubscribes: 8 }),
  Object.freeze({ id: "qual-7", environment: "qualification", lifecycleState: "active", leadSource: "referral", acquisitionChannel: "reengagement", geography: "northeast", recipientRecords: 275, newRecords: 33, unsubscribes: 5 }),
  Object.freeze({ id: "qual-8", environment: "qualification", lifecycleState: "engaged", leadSource: "partner", acquisitionChannel: "welcome_series", geography: "south", recipientRecords: 230, newRecords: 24, unsubscribes: 4 }),
  Object.freeze({ id: "qual-9", environment: "qualification", lifecycleState: "qualified", leadSource: "organic", acquisitionChannel: "promotional", geography: "midwest", recipientRecords: 175, newRecords: 19, unsubscribes: 3 }),
  Object.freeze({ id: "qual-10", environment: "qualification", lifecycleState: "customer", leadSource: "paid_search", acquisitionChannel: "newsletter", geography: "west", recipientRecords: 140, newRecords: 7, unsubscribes: 2 }),

  // Production
  Object.freeze({ id: "prod-1", environment: "production", lifecycleState: "subscribed", leadSource: "organic", acquisitionChannel: "promotional", geography: "midwest", recipientRecords: 6200, newRecords: 890, unsubscribes: 140 }),
  Object.freeze({ id: "prod-2", environment: "production", lifecycleState: "active", leadSource: "paid_search", acquisitionChannel: "newsletter", geography: "west", recipientRecords: 4800, newRecords: 610, unsubscribes: 95 }),
  Object.freeze({ id: "prod-3", environment: "production", lifecycleState: "engaged", leadSource: "referral", acquisitionChannel: "reengagement", geography: "northeast", recipientRecords: 3950, newRecords: 430, unsubscribes: 78 }),
  Object.freeze({ id: "prod-4", environment: "production", lifecycleState: "qualified", leadSource: "partner", acquisitionChannel: "welcome_series", geography: "south", recipientRecords: 2900, newRecords: 310, unsubscribes: 52 }),
  Object.freeze({ id: "prod-5", environment: "production", lifecycleState: "customer", leadSource: "organic", acquisitionChannel: "promotional", geography: "midwest", recipientRecords: 2350, newRecords: 140, unsubscribes: 30 }),
  Object.freeze({ id: "prod-6", environment: "production", lifecycleState: "subscribed", leadSource: "paid_search", acquisitionChannel: "newsletter", geography: "west", recipientRecords: 5700, newRecords: 845, unsubscribes: 125 }),
  Object.freeze({ id: "prod-7", environment: "production", lifecycleState: "active", leadSource: "referral", acquisitionChannel: "reengagement", geography: "northeast", recipientRecords: 4150, newRecords: 505, unsubscribes: 82 }),
  Object.freeze({ id: "prod-8", environment: "production", lifecycleState: "engaged", leadSource: "partner", acquisitionChannel: "welcome_series", geography: "south", recipientRecords: 3400, newRecords: 360, unsubscribes: 61 }),
  Object.freeze({ id: "prod-9", environment: "production", lifecycleState: "qualified", leadSource: "organic", acquisitionChannel: "promotional", geography: "midwest", recipientRecords: 2650, newRecords: 290, unsubscribes: 48 }),
  Object.freeze({ id: "prod-10", environment: "production", lifecycleState: "customer", leadSource: "paid_search", acquisitionChannel: "newsletter", geography: "west", recipientRecords: 2100, newRecords: 115, unsubscribes: 27 }),
]);

// Module-load invariant — fails loudly at import time, matching this
// codebase's established pattern (emailPerformanceMockAdapter's
// assertBroadcastIdsUnique, emailReputationMockAdapter's
// assertPoolInvariants).
(function assertCatalogInvariants() {
  const seenIds = new Set();
  for (const row of AUDIENCE_SEGMENT_ROWS) {
    if (seenIds.has(row.id)) {
      throw new Error(`emailAudienceMockAdapter: duplicate row id "${row.id}".`);
    }
    seenIds.add(row.id);
    if (row.recipientRecords <= 0) {
      throw new Error(`emailAudienceMockAdapter: row "${row.id}" recipientRecords must be positive.`);
    }
    if (row.unsubscribes > row.recipientRecords) {
      throw new Error(
        `emailAudienceMockAdapter: row "${row.id}" unsubscribes cannot exceed recipientRecords (a post-delivery subset).`
      );
    }
    if (row.newRecords < 0 || row.unsubscribes < 0) {
      throw new Error(`emailAudienceMockAdapter: row "${row.id}" newRecords/unsubscribes cannot be negative.`);
    }
  }
})();

// Zero-denominator guard — no NaN/Infinity ever reaches a consumer, same
// helper convention as emailDashboardMockAdapter.js's safeRate.
function safeRate(numerator, denominator) {
  return denominator ? numerator / denominator : 0;
}

function sumBy(rows, key) {
  return rows.reduce((total, row) => total + row[key], 0);
}

// `leadSource`/`acquisitionChannel`/`geography`/`lifecycleState` left
// `undefined` means "not restricted by this dimension" — used to build
// the Lifecycle Distribution funnel and each Segment Breakdown table,
// which each intentionally exclude their OWN dimension's filter so the
// full distribution/breakdown stays visible even when that one dimension
// has an active filter (mirrors emailReputationMockAdapter's
// environmentComparison, which always reports both environments
// regardless of the applied Environment filter).
function filterRows({ environment, leadSource, acquisitionChannel, geography, lifecycleState }) {
  return AUDIENCE_SEGMENT_ROWS.filter((row) => {
    if (environment && environment !== "all" && row.environment !== environment) return false;
    if (leadSource && leadSource !== "all" && row.leadSource !== leadSource) return false;
    if (acquisitionChannel && acquisitionChannel !== "all" && row.acquisitionChannel !== acquisitionChannel) return false;
    if (geography && geography !== "all" && row.geography !== geography) return false;
    if (lifecycleState && lifecycleState !== "all" && row.lifecycleState !== lifecycleState) return false;
    return true;
  });
}

const METRIC_REASON =
  "Derived from a deterministic mock audience/acquisition dataset; no backend audience aggregation endpoint exists yet.";

// Metric Confidence Standard shape — {value, status, reason} — same
// contract KPICard/every other Email adapter already uses.
function buildKpis({ recipientRecords, newRecords, unsubscribes, netGrowth }) {
  return {
    recipient_records: { value: recipientRecords, status: "estimated", reason: METRIC_REASON },
    net_growth: { value: netGrowth, status: "estimated", reason: METRIC_REASON },
    new_records: { value: newRecords, status: "estimated", reason: METRIC_REASON },
    unsubscribe_rate: { value: safeRate(unsubscribes, recipientRecords), status: "estimated", reason: METRIC_REASON },
  };
}

// EmailFunnelCard's established {label, value, status}[] contract.
function buildLifecycleFunnel(rows) {
  return LIFECYCLE_STATES.map((state) => ({
    label: LIFECYCLE_STATE_LABELS[state],
    value: sumBy(
      rows.filter((row) => row.lifecycleState === state),
      "recipientRecords"
    ),
    status: "estimated",
  }));
}

// Raw counts only — EmailSegmentTable derives unsubscribeRate/netGrowth
// from these, the same "derive, don't duplicate" approach
// EmailBroadcastTable already uses for its own rate columns.
function buildDimensionBreakdown(rows, dimension, values, labels) {
  return values.map((value) => {
    const matching = rows.filter((row) => row[dimension] === value);
    return {
      id: value,
      label: labels[value],
      recipientRecords: sumBy(matching, "recipientRecords"),
      newRecords: sumBy(matching, "newRecords"),
      unsubscribes: sumBy(matching, "unsubscribes"),
      status: "estimated",
    };
  });
}

// DASH-EMAIL-010: previously synthesized a 6-period growth trend by
// splitting totalNewRecords/totalUnsubscribes across fixed weight curves —
// a fabricated chronological series explicitly rejected by
// EMAIL-ANALYTICS-SPEC-001 §19 (a split of an aggregate total is not real
// event-time data). No replacement series is substituted; growthTrend is
// now an empty array, and AudienceGrowthTrendCard renders an explicit
// "not yet backed by real event-time/send-cohort data" state.

// Consumed by EmailFiltersControl.jsx to render Audience's four real
// contextual <Select> controls without duplicating this file's dimension
// value/label literals a second time.
export const AUDIENCE_FILTER_OPTIONS = Object.freeze({
  leadSource: LEAD_SOURCES.map((value) => ({ value, label: LEAD_SOURCE_LABELS[value] })),
  acquisitionChannel: ACQUISITION_CHANNELS.map((value) => ({ value, label: ACQUISITION_CHANNEL_LABELS[value] })),
  geography: GEOGRAPHIES.map((value) => ({ value, label: GEOGRAPHY_LABELS[value] })),
  lifecycleState: LIFECYCLE_STATES.map((value) => ({ value, label: LIFECYCLE_STATE_LABELS[value] })),
});

/**
 * getMockEmailAudienceData
 *
 * @param {Object} params
 * @param {'all'|'qualification'|'production'} [params.environment]
 * @param {string} [params.period]
 * @param {{startDate:string,endDate:string}|null} [params.customRange]
 * @param {string} [params.leadSource]
 * @param {string} [params.acquisitionChannel]
 * @param {string} [params.geography]
 * @param {string} [params.lifecycleState]
 * @returns {{
 *   environment: string,
 *   period: string,
 *   customRange: object|null,
 *   filters: {leadSource:string, acquisitionChannel:string, geography:string, lifecycleState:string},
 *   kpis: object,
 *   lifecycleFunnel: Array<{label:string, value:number, status:string}>,
 *   segmentBreakdown: {leadSource:Array, acquisitionChannel:Array, geography:Array, lifecycleState:Array},
 *   growthTrend: Array<{period:string, newRecords:number, unsubscribes:number, netGrowth:number}>,
 * }}
 *
 * environment is additive-union for "all" (qualification's matching rows
 * + production's matching rows), never a separately-tuned baseline —
 * "All >= any filtered subset" holds by construction (subset-sum over the
 * same catalog). lifecycleFunnel and each segmentBreakdown dimension
 * exclude their OWN dimension's filter (see filterRows's comment) so
 * their visualizations stay meaningful even when that exact dimension is
 * the one currently filtered.
 */
export function getMockEmailAudienceData({
  environment = "all",
  period = "mtd",
  customRange = null,
  leadSource = "all",
  acquisitionChannel = "all",
  geography = "all",
  lifecycleState = "all",
} = {}) {
  const filters = { leadSource, acquisitionChannel, geography, lifecycleState };

  const matchingRows = filterRows({ environment, leadSource, acquisitionChannel, geography, lifecycleState });
  const recipientRecords = sumBy(matchingRows, "recipientRecords");
  const newRecords = sumBy(matchingRows, "newRecords");
  const unsubscribes = sumBy(matchingRows, "unsubscribes");
  const netGrowth = newRecords - unsubscribes;

  const lifecycleRows = filterRows({ environment, leadSource, acquisitionChannel, geography });

  return {
    environment,
    period,
    customRange: period === "custom" ? customRange : null,
    filters,
    kpis: buildKpis({ recipientRecords, newRecords, unsubscribes, netGrowth }),
    lifecycleFunnel: buildLifecycleFunnel(lifecycleRows),
    segmentBreakdown: {
      leadSource: buildDimensionBreakdown(
        filterRows({ environment, acquisitionChannel, geography, lifecycleState }),
        "leadSource",
        LEAD_SOURCES,
        LEAD_SOURCE_LABELS
      ),
      acquisitionChannel: buildDimensionBreakdown(
        filterRows({ environment, leadSource, geography, lifecycleState }),
        "acquisitionChannel",
        ACQUISITION_CHANNELS,
        ACQUISITION_CHANNEL_LABELS
      ),
      geography: buildDimensionBreakdown(
        filterRows({ environment, leadSource, acquisitionChannel, lifecycleState }),
        "geography",
        GEOGRAPHIES,
        GEOGRAPHY_LABELS
      ),
      lifecycleState: buildDimensionBreakdown(
        filterRows({ environment, leadSource, acquisitionChannel, geography }),
        "lifecycleState",
        LIFECYCLE_STATES,
        LIFECYCLE_STATE_LABELS
      ),
    },
    // DASH-EMAIL-010: no fabricated trend — see the comment above.
    growthTrend: [],
  };
}

export default getMockEmailAudienceData;
